import { createFileRoute } from '@tanstack/react-router'

import { useBotCreationStore } from '../../../stores'
import { BpSpinner } from '../../../elements'
import { useRef } from 'react'
import { useAsync } from 'react-use'
import { showBotTypeSelectionDialog } from '../../../features/bots/components/BotTypeSelectionDialog'
import { useFeature } from '~/hooks/useFeature'
import { config } from '~/shared'
import { setWorkspacePreference } from '~/services/preferences/workspacePreferences'
import { useVariant } from '@unleash/proxy-client-react'
import type { BotType } from '~/features/bots/components'

export const Route = createFileRoute('/workspaces/$workspaceId/create-bot')({
  component: RouteComponent,
})

function RouteComponent() {
  const { workspaceId } = Route.useParams()
  const createBot = useBotCreationStore((state) => state.createBot)
  const createAdkBot = useBotCreationStore((state) => state.createAdkBot)
  const createVibeBot = useBotCreationStore((state) => state.createVibeBot)
  const navigate = Route.useNavigate()
  const isAdkBotsEnabled = useFeature('adk-bots-from-dashboard')
  const isViberEnabled = useFeature('dashboard-viber')
  const isStudioOnboardingEnabled = useFeature('studio-onboarding')
  const studioOnboardingVariant = useVariant('studio-onboarding')

  const hasRunRef = useRef(false)

  useAsync(async () => {
    if (hasRunRef.current) return
    hasRunRef.current = true

    const botTypes: BotType[] = [
      ...(isViberEnabled ? (['vibe'] as const) : []),
      'studio',
      ...(isAdkBotsEnabled ? (['adk'] as const) : []),
    ]
    const botType = await showBotTypeSelectionDialog({ dismissable: false, botTypes })

    if (botType === 'adk') {
      createAdkBot({
        workspaceId,
        name: 'New Agent',
        onBotCreated: (newBot) =>
          navigate({
            to: '/workspaces/$workspaceId/bots/$botId/new-adk-bot-setup',
            from: '/workspaces/$workspaceId',
            params: { botId: newBot.id },
          }),
      })
    } else if (botType === 'vibe') {
      createVibeBot({
        workspaceId,
        name: 'New Vibe Bot',
        onBotCreated: (newBot) =>
          navigate({
            to: '/workspaces/$workspaceId/bots/$botId/vibe',
            from: '/workspaces/$workspaceId',
            params: { botId: newBot.id },
          }),
      })
    } else {
      createBot({
        workspaceId,
        entrypoint: 'post_fork',
        onBotCreated: async (newBot) => {
          if (isStudioOnboardingEnabled && studioOnboardingVariant.name !== 'dashboard') {
            await setWorkspacePreference(
              {
                path: 'isStudioOnboarding/$botId',
                params: { botId: newBot.id },
                value: {
                  entryPoint: 'post_fork',
                  version: studioOnboardingVariant.name ?? 'default',
                },
              },
              workspaceId
            )
            window.location.href = `${config.studioBaseUrl}/${newBot.id}`
          } else {
            navigate({
              to: '/workspaces/$workspaceId/bots/$botId/new-bot',
              from: '/workspaces/$workspaceId',
              params: { botId: newBot.id },
            })
          }
        },
      })
    }
  }, [])

  return (
    <div className="flex h-[80vh] w-full items-center justify-center">
      <BpSpinner className="h-20 w-20" />
    </div>
  )
}
